import { TJobFormData } from "./CreateJobAndSchedule.model";

const DRAFT_KEY_PREFIX = "dcs.job-draft.";

/**
 * Fields kept out of the draft, for one of three reasons:
 *
 * - they hold File objects, which cannot be serialised to storage;
 * - they are detected from the browser rather than entered, so a stored copy
 *   could only ever restore a stale one; or
 * - the server owns them (the "view only" group in the model). They are re-fetched
 *   on every load, so a draft carrying them would only ever restore stale copies
 *   over fresh data.
 *
 * `leadlaborGoalHours` is deliberately absent: it is seeded from the lead but is an
 * editable job field, so a user's edit to it is worth preserving.
 */
const UNSAVED_FIELDS: Array<keyof TJobFormData> = [
  // Cannot be serialised.
  "attachments",
  "attachment2Path",
  // Detected from the browser, not entered: there is no control that sets it,
  // and a stored zone could only ever restore one the user has since left.
  // (A draft saved before the Time Zone dropdown was removed still holds one
  // of its placeholder values - resolveTimeZone is what catches those.)
  "timeZone",
  // Server-owned, re-fetched on every load.
  "customerCategory",
  "clientId",
  "clientFirstName",
  "clientLastName",
  "clientCompanyName",
  "clientContactName",
  "clientFax",
  "clientPhoneNumber",
  "cientContactEmail",
  "leadAddress",
  "attachment1",
  "leadAdditionalNotes",
  "sowFrequencies",
];

/** JSON has no date type, so these need rebuilding on the way back in. */
const DATE_FIELDS: Array<keyof TJobFormData> = ["startTime", "endTime", "recurrenceEndDate"];

const keyFor = (leadId: string) => `${DRAFT_KEY_PREFIX}${leadId}`;

export const stripUnsavableFields = (values: TJobFormData): Partial<TJobFormData> => {
  const draft: Partial<TJobFormData> = { ...values };

  UNSAVED_FIELDS.forEach((field) => delete draft[field]);

  return draft;
};

export const saveJobDraft = (leadId: string | undefined, values: TJobFormData): void => {
  // Without a lead id every draft would share the key "undefined" and leak between leads.
  if (!leadId) {
    return;
  }

  try {
    window.localStorage.setItem(keyFor(leadId), JSON.stringify(stripUnsavableFields(values)));
  } catch {
    // A full or unavailable store must not break the form.
  }
};

export const loadJobDraft = (leadId: string | undefined): Partial<TJobFormData> | null => {
  if (!leadId) {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(keyFor(leadId));

    if (!stored) {
      return null;
    }

    const draft = JSON.parse(stored) as Partial<TJobFormData>;

    DATE_FIELDS.forEach((field) => {
      const value = draft[field];
      if (value) {
        (draft as any)[field] = new Date(value as any);
      }
    });

    return draft;
  } catch {
    return null;
  }
};

export const clearJobDraft = (leadId: string | undefined): void => {
  if (!leadId) {
    return;
  }

  try {
    window.localStorage.removeItem(keyFor(leadId));
  } catch {
    // Nothing useful to do.
  }
};
